import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent } from '@/components/ui/card';
import { toast } from 'sonner';
import { Loader2, Mail, ArrowLeft } from 'lucide-react';

const LOGO_BLACK = '/logo-biounica-nero.png';

export default function ResetPasswordPage() {
  const { resetPassword } = useAuth();
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await resetPassword(email);
      setSent(true);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Errore nell\'invio della richiesta');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#efe8da] flex items-center justify-center p-4" data-testid="reset-password-page">
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <img src={LOGO_BLACK} alt="Biounica" className="h-14 mx-auto mb-4" />
          <h1 className="font-heading text-2xl sm:text-3xl font-bold text-[#12100c]">Recupera password</h1>
          <p className="text-sm text-[#4a4238] mt-1">
            Inserisci la tua email per ricevere il link di reset
          </p>
        </div>

        <Card className="rounded-2xl shadow-sm border-gray-100">
          <CardContent className="p-6 sm:p-8">
            {sent ? (
              <div className="text-center py-4" data-testid="reset-password-sent">
                <div className="w-14 h-14 bg-[#5f7d52]/10 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Mail className="w-6 h-6 text-[#4a5f43]" />
                </div>
                <h3 className="font-medium text-[#12100c] mb-2">Email inviata!</h3>
                <p className="text-sm text-[#4a4238] mb-6">
                  Se l'email è registrata, riceverai un link per reimpostare la password. Controlla anche la cartella spam.
                </p>
                <Link
                  to="/login"
                  className="inline-flex items-center gap-2 text-sm text-[#4a5f43] font-medium hover:underline"
                  data-testid="back-to-login-link"
                >
                  <ArrowLeft className="w-4 h-4" />
                  Torna al login
                </Link>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="space-y-5">
                <div>
                  <Label htmlFor="email" className="text-[#12100c] font-medium text-sm">
                    Email
                  </Label>
                  <Input
                    id="email"
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="la-tua@email.it"
                    required
                    className="mt-1.5 rounded-xl border-gray-300 focus:border-[#c6a15b] focus:ring-[#c6a15b]"
                    data-testid="reset-email-input"
                  />
                </div>

                <Button
                  type="submit"
                  disabled={loading}
                  className="w-full bg-[#12100c] hover:bg-[#5f7d52] text-white rounded-full h-11 font-medium transition-colors"
                  data-testid="reset-submit-btn"
                >
                  {loading && <Loader2 className="w-4 h-4 animate-spin mr-2" />}
                  Invia link di reset
                </Button>

                <div className="text-center">
                  <Link
                    to="/login"
                    className="inline-flex items-center gap-2 text-sm text-[#4a4238] hover:text-[#12100c]"
                    data-testid="back-to-login-link"
                  >
                    <ArrowLeft className="w-4 h-4" />
                    Torna al login
                  </Link>
                </div>
              </form>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
